import React, { createContext, useContext, useState, useRef } from 'react';

const ExportContext = createContext();

export function ExportProvider({ children }) {
  const [exports, setExports] = useState([]);
  const controllersRef = useRef({});

  const startExport = async ({
    idRemoto, format, filters, sorting,
    globalFilter, colsHeaders, label, customUrl
  }) => {
    const exportId = `${idRemoto || 'custom'}_${Date.now()}`;
    const controller = new AbortController();
    controllersRef.current[exportId] = controller;

    setExports(prev => [...prev, {
      id: exportId,
      label: label || `Query ${idRemoto}`,
      format: format.toUpperCase(),
      status: 'loading',
      detail: null,
    }]);

    try {
      let url;
      if (customUrl) {
        url = customUrl;
      } else {
        let currentFilters = [...(filters ?? [])];
        currentFilters = currentFilters.filter(f =>
          Object.keys(colsHeaders ?? {}).some(k => k === f.id)
        );
        const params = new URLSearchParams({
          id: idRemoto,
          format,
          filters: JSON.stringify(currentFilters),
          sorting: JSON.stringify(sorting ?? []),
          globalFilter: globalFilter ?? '',
        });
        url = `${process.env.REACT_APP_API_URL}/querysextgrillaexp/export?${params}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': localStorage.getItem('token') },
        signal: controller.signal,
      });

      // Verificar si el backend respondió con JSON (error, ej: límite Excel)
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const json = await response.json();
        const msg = json.message || 'Error al exportar';
        setExports(prev => prev.map(e =>
          e.id === exportId ? { ...e, status: 'error', detail: msg } : e
        ));
        delete controllersRef.current[exportId];
        setTimeout(() => {
          setExports(prev => prev.filter(e => e.id !== exportId));
        }, 6000);
        return;
      }

      if (!response.ok) throw new Error('Error al exportar');

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match
        ? match[1]
        : `export_${idRemoto || 'deuda'}_${Date.now()}.${format}`;

      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);

      setExports(prev => prev.map(e =>
        e.id === exportId ? { ...e, status: 'done' } : e
      ));
      delete controllersRef.current[exportId];
      setTimeout(() => {
        setExports(prev => prev.filter(e => e.id !== exportId));
      }, 5000);

    } catch (err) {
      if (err.name === 'AbortError') {
        setExports(prev => prev.map(e =>
          e.id === exportId ? { ...e, status: 'cancelled' } : e
        ));
        delete controllersRef.current[exportId];
        setTimeout(() => {
          setExports(prev => prev.filter(e => e.id !== exportId));
        }, 3000);
        return;
      }
      console.error('Export error:', err);
      setExports(prev => prev.map(e =>
        e.id === exportId ? { ...e, status: 'error', detail: err.message } : e
      ));
      delete controllersRef.current[exportId];
      setTimeout(() => {
        setExports(prev => prev.filter(e => e.id !== exportId));
      }, 6000);
    }
  };

  const cancelExport = (exportId) => {
    const controller = controllersRef.current[exportId];
    if (controller) {
      controller.abort();
      delete controllersRef.current[exportId];
    }
  };

  const dismissExport = (exportId) => {
    setExports(prev => prev.filter(e => e.id !== exportId));
    const controller = controllersRef.current[exportId];
    if (controller) {
      controller.abort();
      delete controllersRef.current[exportId];
    }
  };

  return (
    <ExportContext.Provider value={{ exports, startExport, cancelExport, dismissExport }}>
      {children}
    </ExportContext.Provider>
  );
}

export function useExport() {
  return useContext(ExportContext);
}
